import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import type { ReactNode } from 'react';
import { TeamsAuthProvider, AuthError } from '@annoteer/platform-adapter/TeamsAuthProvider';
import { usePlatform } from './PlatformContext.js';

type AuthStatus = 'initializing' | 'authenticated' | 'consent_required' | 'error';

interface AuthUser {
  oid: string;
  name: string;
  email?: string;
}

interface AuthContextValue {
  status: AuthStatus;
  token: string | null;
  user: AuthUser | null;
  error: string | null;
  authFetch: (url: string, init?: RequestInit) => Promise<Response>;
  getToken: () => string | null;
  retryAuth: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const SERVER_URL = import.meta.env.VITE_SERVER_URL || '';

interface ExchangeResponse {
  accessToken: string;
  expiresIn: number;
  user?: AuthUser;
  error?: string;
  details?: string;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const { isInitialized: platformReady } = usePlatform();
  const [status, setStatus] = useState<AuthStatus>('initializing');
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [error, setError] = useState<string | null>(null);
  const authProviderRef = useRef(new TeamsAuthProvider());
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const exchangeToken = useCallback(async (ssoToken: string): Promise<ExchangeResponse> => {
    // 15s timeout so a hung server doesn't leave the client stuck forever
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);
    let response: Response;
    try {
      response = await fetch(`${SERVER_URL}/api/auth/exchange`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${ssoToken}`,
        },
        signal: controller.signal,
      });
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        throw new Error('Token exchange timed out after 15s (server unreachable?)');
      }
      throw err;
    } finally {
      clearTimeout(timeoutId);
    }

    const data = (await response.json()) as ExchangeResponse;

    if (response.status === 403 && data.error === 'consent_required') {
      throw new AuthError('Consent required', 'consent_required');
    }

    if (!response.ok) {
      const base = data.error ?? `Exchange failed: ${response.status}`;
      const full = data.details ? `${base} — ${data.details}` : base;
      throw new Error(full);
    }

    return data;
  }, []);

  // scheduleRefresh calls performAuth, and performAuth calls scheduleRefresh.
  // Route the timer through a ref so neither has to list the other as a
  // dependency, and so the timer always fires the CURRENT performAuth rather
  // than whichever closure existed when the timer was armed. Today the whole
  // chain happens to be stable, so a stale closure would be harmless — but a
  // refresh that silently reuses an old token is precisely the failure this
  // codebase has been bitten by before (CLAUDE.md gotcha #27), and it would
  // only surface an hour into a live meeting.
  const performAuthRef = useRef<() => Promise<void>>(async () => {});

  const scheduleRefresh = useCallback((expiresIn: number) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
    // Refresh 5 minutes before expiry, minimum 30 seconds
    const refreshMs = Math.max((expiresIn - 300) * 1000, 30_000);
    refreshTimerRef.current = setTimeout(() => {
      void performAuthRef.current();
    }, refreshMs);
  }, []);

  const performAuth = useCallback(async () => {
    setStatus('initializing');
    setError(null);
    try {
      // Clear cache on retry so we don't reuse a stale SSO token
      authProviderRef.current.clearCache();
      // Try Teams SSO first
      const ssoToken = await authProviderRef.current.getSsoToken();
      const data = await exchangeToken(ssoToken);
      setToken(data.accessToken);
      setUser(data.user ?? null);
      setStatus('authenticated');
      setError(null);
      scheduleRefresh(data.expiresIn);
    } catch (err: unknown) {
      // Teams SDK not available OR we're running outside Teams on localhost
      // (where getAuthToken silently hangs and times out → consent_required).
      // In both cases, fall through to the dev-mode token exchange BEFORE
      // the consent_required early-return, otherwise dev mode never fires.
      const isLocalDev =
        typeof window !== 'undefined' &&
        /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
      // Don't rely on `err instanceof AuthError` — the TeamsAuthProvider is
      // loaded as a compiled dist/ module through the workspace resolver and
      // the AuthError imported here may not be the same constructor reference
      // at runtime. Check the shape instead.
      const errCode = (err as { code?: string } | null)?.code;
      const shouldTryDevFallback =
        errCode === 'sdk_not_available' || (isLocalDev && errCode === 'consent_required');
      if (shouldTryDevFallback) {
        try {
          const data = await exchangeToken('dev-sso-token');
          setToken(data.accessToken);
          setUser(data.user ?? { oid: 'dev-user', name: 'Dev User' });
          setStatus('authenticated');
          setError(null);
          scheduleRefresh(data.expiresIn);
          return;
        } catch {
          // Server also not in dev mode — fall through to the error branch
        }
      }

      // Production consent flow: show banner so the user can grant consent
      if (errCode === 'consent_required') {
        setStatus('consent_required');
        setError('Azure AD consent required');
        return;
      }

      const message = err instanceof Error ? err.message : 'Authentication failed';
      setError(message);
      setStatus('error');
    }
  }, [exchangeToken, scheduleRefresh]);

  performAuthRef.current = performAuth;

  useEffect(() => {
    // Wait for PlatformProvider to finish app.initialize() before calling
    // authentication.getAuthToken(), otherwise the Teams SDK throws
    // "The library has not yet been initialized".
    if (!platformReady) return;
    performAuth();
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [platformReady, performAuth]);

  const getToken = useCallback(() => token, [token]);

  const authFetch = useCallback(
    async (url: string, init?: RequestInit): Promise<Response> => {
      if (!token) {
        throw new Error(
          `Cannot make authenticated request to ${url}: no auth token (status=${status}, error=${error ?? 'none'})`,
        );
      }
      const headers = new Headers(init?.headers);
      headers.set('Authorization', `Bearer ${token}`);

      const response = await fetch(url, { ...init, headers });

      // On 401: attempt silent refresh and retry once
      if (response.status === 401 && token) {
        try {
          const ssoToken = await authProviderRef.current.getSsoToken();
          const data = await exchangeToken(ssoToken);
          setToken(data.accessToken);
          setUser(data.user ?? null);
          scheduleRefresh(data.expiresIn);

          // Retry with new token
          const retryHeaders = new Headers(init?.headers);
          retryHeaders.set('Authorization', `Bearer ${data.accessToken}`);
          return fetch(url, { ...init, headers: retryHeaders });
        } catch {
          setStatus('error');
          setError('Session expired');
        }
      }

      // On 403 consent_required
      if (response.status === 403) {
        try {
          const body = (await response.clone().json()) as { error?: string };
          if (body.error === 'consent_required') {
            setStatus('consent_required');
          }
        } catch {
          // Not JSON — ignore
        }
      }

      return response;
    },
    [token, status, error, exchangeToken, scheduleRefresh],
  );

  const retryAuth = useCallback(() => {
    void performAuth();
  }, [performAuth]);

  return (
    <AuthContext.Provider value={{ status, token, user, error, authFetch, getToken, retryAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
